/* eslint-disable max-lines */
import { useZoneOccupancy } from '@hakimo-ui/hakimo/data-access';
import {
  PresenceLiveness,
  PresenceRule,
  PresenceRuleType,
  PresenceZone,
  PresenceZoneKind,
} from '@hakimo-ui/hakimo/types';
import { SelectMenu } from '@hakimo-ui/hakimo/ui-elements';
import { eventTracker } from '@hakimo-ui/hakimo/util';
import {
  Alert,
  Button,
  InputField,
  Modal,
  Toggle,
} from '@hakimo-ui/shared/ui-base';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { LineCrossingFields } from './LineCrossingFields';
import {
  countHint,
  countLabel,
  defaultRule,
  describeRule,
  isLineRule,
  livenessHint,
  RULE_TYPE_OPTIONS,
  ruleForSave,
  RuleTypeOption,
  ruleTargetsZone,
  usesCount,
  usesMinutes,
  usesWindow,
  validateRule,
  withType,
  zoneKindFor,
  zoneLabel,
} from './ruleSentence';

interface ZoneOption {
  id: string;
  name: string;
  zoneName: string;
  kind: PresenceZoneKind;
}

interface Props {
  cameraId: string;
  zones: PresenceZone[];
  rule?: PresenceRule;
  liveness?: PresenceLiveness;
  isSaving: boolean;
  error?: string | null;
  onSave: (rule: PresenceRule) => void;
  onClose: () => void;
}

export function AddEditPresenceRule(props: Props) {
  const { cameraId, zones, rule, liveness, isSaving, error, onSave, onClose } =
    props;
  const isEditing = !!rule;

  // Named zones first; the whole camera last, as "anywhere in view".
  const allOptions: ZoneOption[] = useMemo(
    () =>
      [...zones]
        .sort((a, b) => Number(a.isDefault) - Number(b.isDefault))
        .map((z) => ({
          id: z.id,
          name: z.isDefault
            ? `${zoneLabel(z.name)} (anywhere in view)`
            : z.name,
          zoneName: z.name,
          kind: z.kind,
        })),
    [zones]
  );
  const zoneKinds = useMemo(
    () => Object.fromEntries(allOptions.map((z) => [z.id, z.kind])),
    [allOptions]
  );
  const optionsFor = (type: PresenceRuleType) =>
    allOptions.filter((z) => z.kind === zoneKindFor(type));

  const [formValue, setFormValue] = useState<PresenceRule>(() => {
    if (!rule) {
      const first = optionsFor('max_occupancy')[0];
      return defaultRule(
        'max_occupancy',
        first ? { id: first.id, name: first.zoneName } : { id: null, name: '' },
        cameraId
      );
    }
    // A rule written before ids carries only a zone name, and the camera save
    // refuses that. Resolve it here so opening the rule is enough to fix it.
    const match = optionsFor(rule.type).find((z) =>
      ruleTargetsZone(rule, { id: z.id, name: z.zoneName })
    );
    return rule.zoneId || !match ? rule : { ...rule, zoneId: match.id };
  });

  const isLine = isLineRule(formValue.type);
  const zoneOptions = optionsFor(formValue.type);
  const typeOption = RULE_TYPE_OPTIONS.find((o) => o.id === formValue.type);
  const zoneOption = zoneOptions.find((z) =>
    ruleTargetsZone(formValue, { id: z.id, name: z.zoneName })
  );

  // The peak the limit is judged against is this zone's own, over the same
  // day the occupancy card draws -- the last count seen in some other zone
  // says nothing about what to allow here.
  const since = useMemo(
    () => dayjs().subtract(24, 'hour').startOf('hour').toISOString(),
    []
  );
  const occupancy = useZoneOccupancy(
    formValue.zoneId ?? '',
    { from: since, bucket: '15m' },
    !!formValue.zoneId && formValue.type === 'max_occupancy'
  );
  const peakToday = useMemo(() => {
    const observed = (occupancy.data?.items ?? []).filter(
      (b) => b.observedSeconds > 0
    );
    return observed.length ? Math.max(...observed.map((b) => b.max)) : null;
  }, [occupancy.data]);

  const problem = validateRule(formValue, zoneKinds);

  // Switching between a line rule and an area rule changes what the zone
  // picker may hold; move to the first fitting zone rather than leave the
  // rule pointing at one of the wrong kind.
  const changeType = (type: PresenceRuleType) =>
    setFormValue((prev) => {
      const next = withType(prev, type);
      if (zoneKindFor(prev.type) === zoneKindFor(type)) return next;
      const first = optionsFor(type)[0];
      return {
        ...next,
        zoneId: first?.id ?? null,
        zoneName: first?.zoneName ?? '',
      };
    });
  const minutesCaution = livenessHint(liveness, formValue);
  const countCaution = countHint(formValue, peakToday);

  const numberUpdater =
    (name: 'count' | 'thresholdMinutes' | 'windowMinutes') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const value = raw === '' ? 0 : Number(raw);
      setFormValue((prev) => ({ ...prev, [name]: value }));
    };

  const actions = (
    <>
      <Button
        variant="primary"
        onClick={() => onSave(ruleForSave(formValue))}
        disabled={!!problem || isSaving}
        loading={isSaving}
        onSideEffect={eventTracker(
          isEditing ? 'submit_update_presence_rule' : 'submit_add_presence_rule'
        )}
      >
        {isEditing ? 'Save' : 'Add rule'}
      </Button>
      <Button
        onClick={onClose}
        onSideEffect={eventTracker(
          isEditing ? 'cancel_update_presence_rule' : 'cancel_add_presence_rule'
        )}
      >
        Cancel
      </Button>
    </>
  );

  return (
    <Modal
      title={isEditing ? 'Edit presence rule' : 'Add presence rule'}
      open
      onClose={onClose}
      footer={actions}
    >
      {error && (
        <div className="mt-4 px-8">
          <Alert type="error">{error}</Alert>
        </div>
      )}
      <div className="w-[36rem] space-y-5 p-8">
        <div className="space-y-1">
          <SelectMenu<RuleTypeOption>
            label="Alert when"
            value={typeOption}
            items={RULE_TYPE_OPTIONS}
            onChange={(option) => changeType(option.id as PresenceRuleType)}
            displayValue={(item) => item?.label ?? ''}
            id={(item) => item.id}
          />
          {typeOption && (
            <p className="text-onlight-text-3 dark:text-ondark-text-3 text-xs">
              {typeOption.hint}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <SelectMenu<ZoneOption>
            label={isLine ? 'Line' : 'Zone'}
            value={zoneOption}
            items={zoneOptions}
            onChange={(option) =>
              setFormValue((prev) => ({
                ...prev,
                zoneId: option.id,
                zoneName: option.zoneName,
              }))
            }
            displayValue={(item) => item?.name ?? ''}
            id={(item) => item.id}
            placeholder={isLine ? 'Choose a line' : 'Choose a zone'}
          />
          {isLine && zoneOptions.length === 0 && (
            <p className="text-onlight-text-3 dark:text-ondark-text-3 text-xs">
              No lines on this camera yet. Draw one in the zones editor above.
            </p>
          )}
        </div>

        {isLine && (
          <LineCrossingFields
            rule={formValue}
            onChange={(patch) =>
              setFormValue((prev) => ({ ...prev, ...patch }))
            }
          />
        )}

        {usesCount(formValue.type) && (
          <div className="space-y-1">
            <InputField
              type="number"
              label={countLabel(formValue.type) ?? ''}
              value={String(formValue.count)}
              onChange={numberUpdater('count')}
              min="1"
            />
            {countCaution && (
              <p className="text-onlight-text-3 dark:text-ondark-text-3 text-xs">
                {countCaution}
              </p>
            )}
          </div>
        )}

        {usesMinutes(formValue.type) && (
          <div className="space-y-1">
            <InputField
              type="number"
              label={
                formValue.type === 'max_occupancy'
                  ? 'For at least this many minutes (0 = immediately)'
                  : 'For at least this many minutes'
              }
              value={String(formValue.thresholdMinutes)}
              onChange={numberUpdater('thresholdMinutes')}
              min="0"
            />
            {minutesCaution && (
              <p className="text-onlight-text-3 dark:text-ondark-text-3 text-xs">
                {minutesCaution}
              </p>
            )}
          </div>
        )}

        {usesWindow(formValue) && (
          <InputField
            type="number"
            label="Within this many minutes (at most 60)"
            value={String(formValue.windowMinutes)}
            onChange={numberUpdater('windowMinutes')}
            min="1"
            max="60"
          />
        )}

        <Toggle
          label="Enabled"
          enabled={formValue.enabled}
          onChange={(enabled) => setFormValue((prev) => ({ ...prev, enabled }))}
        />

        <div className="border-onlight-line-2 dark:border-ondark-line-2 rounded-md border px-3 py-2">
          <p className="text-onlight-text-3 dark:text-ondark-text-3 text-xs uppercase tracking-wide">
            This rule
          </p>
          <p className="text-onlight-text-1 dark:text-ondark-text-1 text-sm">
            {problem ?? describeRule(formValue)}
          </p>
        </div>
      </div>
    </Modal>
  );
}

export default AddEditPresenceRule;
